import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { api, formatPrice, variantLabel } from "../lib/api";
import { ProductVisual } from "../components/ProductVisual";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const QTY_ICON_SIZE = 13;

const EmptyCart = () => (
  <div className="pt-44 pb-32 text-center px-6" data-testid="cart-page-empty">
    <img src="/brand/goat-badge.jpg" alt="" className="mx-auto h-20 w-20 rounded-full object-cover border border-capra-bronze/40 opacity-70 mb-6" />
    <h1 className="font-display text-3xl text-capra-ivory mb-3">Your cart awaits.</h1>
    <p className="text-sm text-capra-muted mb-8">The collection is ready when you are.</p>
    <Link
      to="/new-collection"
      className="inline-block px-9 py-4 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-capra-amber transition-colors"
    >
      Explore Collection
    </Link>
  </div>
);

const CartLine = ({ item, removeItem, updateQuantity }) => {
  const label = variantLabel(item);
  return (
    <div className="flex gap-6 border-b border-white/5 pb-8" data-testid={`cart-page-item-${item.slug}`}>
      <Link to={`/product/${item.slug}`} className="w-28 h-36 shrink-0 overflow-hidden">
        <ProductVisual product={item} emblemSize="small" />
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-3">
          <Link to={`/product/${item.slug}`} className="font-display text-lg text-capra-ivory hover:text-capra-bronze transition-colors">
            {item.name}
          </Link>
          <button
            data-testid={`cart-page-remove-${item.slug}`}
            onClick={() => removeItem(item.key)}
            className="text-capra-muted hover:text-capra-copper transition-colors shrink-0"
            aria-label="Remove"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        {label && <p className="text-xs text-capra-muted mt-1.5">{label}</p>}
        <div className="flex items-center justify-between mt-5">
          <div className="flex items-center border border-white/15">
            <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="px-3 py-1.5 text-capra-muted hover:text-capra-ivory" aria-label="Decrease">
              <Minus size={QTY_ICON_SIZE} />
            </button>
            <span className="px-3 text-sm text-capra-ivory">{item.quantity}</span>
            <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="px-3 py-1.5 text-capra-muted hover:text-capra-ivory" aria-label="Increase">
              <Plus size={QTY_ICON_SIZE} />
            </button>
          </div>
          <p className="text-capra-gold">{formatPrice(item.price * item.quantity)}</p>
        </div>
      </div>
    </div>
  );
};

const OrderSummary = ({ subtotal, user, guestEmail, setGuestEmail, onCheckout, busy }) => (
  <div className="leather-panel p-8 space-y-6 sticky top-28" data-testid="order-summary">
    <h2 className="font-display text-xl text-capra-ivory">Order Summary</h2>
    <div className="space-y-3 text-sm">
      <div className="flex justify-between text-capra-muted">
        <span>Subtotal</span>
        <span className="text-capra-ivory" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
      </div>
      <div className="flex justify-between text-capra-muted">
        <span>Shipping</span>
        <span className="text-capra-bronze">Complimentary</span>
      </div>
    </div>
    <div className="hairline" />
    <div className="flex justify-between items-baseline">
      <span className="text-[11px] uppercase tracking-[0.22em] text-capra-muted">Total</span>
      <span className="font-display text-2xl text-capra-ivory" data-testid="cart-total">{formatPrice(subtotal)}</span>
    </div>
    {!user && (
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-capra-muted mb-2">Guest checkout — email for confirmation</p>
        <input
          data-testid="guest-email-input"
          type="email"
          value={guestEmail}
          onChange={(e) => setGuestEmail(e.target.value)}
          placeholder="you@email.com"
          className="w-full bg-capra-night/60 border border-white/10 px-4 py-3 text-sm text-capra-ivory placeholder:text-capra-muted/50 outline-none focus:border-capra-bronze transition-colors"
        />
        <p className="mt-2 text-[11px] text-capra-muted">
          Or <Link to="/login" className="text-capra-bronze hover:text-capra-amber">sign in</Link> for order history.
        </p>
      </div>
    )}
    <button
      data-testid="checkout-button"
      onClick={onCheckout}
      disabled={busy}
      className="w-full py-4 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-capra-amber transition-colors disabled:opacity-60"
    >
      {busy ? "Preparing…" : "Proceed to Checkout"}
    </button>
    <p className="text-center text-[10px] tracking-[0.15em] uppercase text-capra-muted/70">Secure payment via Stripe</p>
  </div>
);

export default function CartPage() {
  const { items, removeItem, updateQuantity, subtotal } = useCart();
  const { user } = useAuth();
  const [guestEmail, setGuestEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    if (!user && !guestEmail) {
      toast.error("Please enter your email for order confirmation.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        origin_url: window.location.origin,
        email: user ? undefined : guestEmail,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout could not be started.");
      setBusy(false);
    }
  };

  if (items.length === 0) return <EmptyCart />;

  return (
    <div className="pt-36 lg:pt-44 pb-24 px-6 lg:px-10 max-w-[1200px] mx-auto" data-testid="cart-page">
      <h1 className="font-display text-4xl sm:text-5xl text-capra-ivory mb-12">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-8">
          {items.map((item) => (
            <CartLine key={item.key} item={item} removeItem={removeItem} updateQuantity={updateQuantity} />
          ))}
        </div>
        <div>
          <OrderSummary
            subtotal={subtotal}
            user={user}
            guestEmail={guestEmail}
            setGuestEmail={setGuestEmail}
            onCheckout={checkout}
            busy={busy}
          />
        </div>
      </div>
    </div>
  );
}
